import { type SendFeedbackRequestError } from '@aikoa/api'
import { useTranslations, type TFunction } from '@aikoa/i18n/react'
import { router } from 'expo-router'
import { type AlertAction, useAlert } from '~/components/GlobalAlert'

const getFeedbackRequestErrorMessage = (
  t: TFunction<'feedback'>,
  error: SendFeedbackRequestError
): { message: string; actions?: AlertAction[] } => {
  switch (error) {
    case 'CONTACT_NOT_FOUND':
      return {
        message: t(($) => $.errors.contactNotFound),
      }
    case 'CONTACT_DISABLED':
      return {
        message: t(($) => $.errors.contactDisabled),
      }
    case 'ASSET_NOT_FOUND':
      return {
        message: t(($) => $.errors.assetNotFound),
      }
    case 'USER_PROFILE_NOT_FOUND':
      return {
        message: t(($) => $.errors.incompleteProfile),
        actions: [
          {
            key: 'user-profile-not-found',
            // eslint-disable-next-line i18next/no-literal-string
            label: 'Korjaa profiili',
            buttonVariant: 'primary',
            buttonSize: 'lg',
            onPress: () => router.push('/(authenticated)/profile'),
          },
        ],
      }
    case 'FEEDBACK_REQUEST_MINIMUM_TIMEOUT':
      return {
        message: t(($) => $.errors.contactAlreadyRequested),
      }
    case 'FEEDBACK_RECEIVED':
      return {
        message: t(($) => $.errors.contactAlreadyAnswered),
      }
    case 'CONTACT_NO_SENDING_CHANNEL':
      return {
        message: t(($) => $.errors.contactNoContactInfo),
        actions: [
          {
            key: 'add-contact-channel',
            // eslint-disable-next-line i18next/no-literal-string
            label: 'Muokkaa kontaktia',
            buttonVariant: 'primary',
            buttonSize: 'lg',
            onPress: () => router.back(),
          },
        ],
      }
    case 'CONTACT_NO_EMAIL_FOR_METHOD':
      return {
        message: t(($) => $.errors.contactNoEmail),
        actions: [
          {
            key: 'add-contact-email',
            // eslint-disable-next-line i18next/no-literal-string
            label: 'Muokkaa kontaktia',
            buttonVariant: 'primary',
            buttonSize: 'lg',
            onPress: () => router.back(),
          },
        ],
      }
    case 'CONTACT_NO_PHONE_FOR_METHOD':
      return {
        message: t(($) => $.errors.contactNoPhone),
        actions: [
          {
            key: 'add-contact-phone',
            // eslint-disable-next-line i18next/no-literal-string
            label: 'Muokkaa kontaktia',
            buttonVariant: 'primary',
            buttonSize: 'lg',
            onPress: () => router.back(),
          },
        ],
      }
    case 'FEEDBACK_ALREADY_SENT':
      return {
        message: t(($) => $.errors.requestAlreadySent),
      }
    case 'FEEDBACK_ALREADY_ANSWERED':
      return {
        message: t(($) => $.errors.alreadyAnsweredByContact),
      }
    case 'SEND_EMAIL_FAILED':
      return {
        message: t(($) => $.errors.emailSendFailed),
      }
    case 'SEND_SMS_FAILED':
      return {
        message: t(($) => $.errors.smsSendFailed),
      }
    case 'SEND_ALL_METHODS_FAILED':
      return {
        message: t(($) => $.errors.allMethodsFailed),
      }
    case 'GENERAL_ERROR':
    default:
      return {
        message: t(($) => $.errors.genericError),
      }
  }
}

export function useFeedbackErrorMessage() {
  const { t } = useTranslations('feedback')
  const { showAlert } = useAlert()

  return (error: SendFeedbackRequestError, onClose?: () => void) => {
    const alertContent = getFeedbackRequestErrorMessage(t, error)

    showAlert({
      type: 'error',
      title: t(($) => $.errors.title),
      description: alertContent.message,
      actions: alertContent.actions,
      cancelButton: {
        label: 'OK',
        buttonVariant: !alertContent.actions ? 'primary' : 'ghost',
        buttonSize: 'lg',
        onPress: onClose,
      },
    })
  }
}
